import moment from 'moment';

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

export type TimeOffDateTimeInput = {
	startDate?: string | Date | null;
	endDate?: string | Date | null;
	startTime?: string | null;
	endTime?: string | null;
};

const toDateString = (value?: string | Date | null): string | null => {
	if (!value) return null;
	if (value instanceof Date) {
		return moment(value).isValid() ? moment(value).format('YYYY-MM-DD') : null;
	}
	const trimmed = String(value).trim();
	if (!trimmed) return null;
	if (DATE_ONLY.test(trimmed)) return trimmed;
	const parsed = moment(trimmed);
	return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null;
};

const normalizeTime = (value?: string | null): string | null => {
	if (!value) return null;
	const trimmed = value.trim();
	if (!trimmed) return null;
	const parsed = moment(trimmed, ['HH:mm:ss', 'HH:mm'], true);
	return parsed.isValid() ? parsed.format('HH:mm:ss') : null;
};

export const hasTimeOffTimeFields = (
	startTime?: string | null,
	endTime?: string | null
): boolean => Boolean(normalizeTime(startTime) || normalizeTime(endTime));

export const getTimeOffTimeValidationError = ({
	startDate,
	endDate,
	startTime,
	endTime,
}: TimeOffDateTimeInput): string | null => {
	const normalizedStart = normalizeTime(startTime);
	const normalizedEnd = normalizeTime(endTime);

	if (!normalizedStart && !normalizedEnd) return null;
	if (!normalizedStart || !normalizedEnd) {
		return 'Start time and end time are both required for a partial-day request';
	}

	const startDateStr = toDateString(startDate);
	const endDateStr = toDateString(endDate);
	if (!startDateStr || !endDateStr) return null;

	const start = moment(`${startDateStr}T${normalizedStart}`);
	const end = moment(`${endDateStr}T${normalizedEnd}`);
	if (!start.isValid() || !end.isValid()) return 'Enter a valid start and end time';
	if (!end.isAfter(start)) return 'End time must be later than start time';

	return null;
};

export const buildTimeOffDateTimes = ({
	startDate,
	endDate,
	startTime,
	endTime,
}: TimeOffDateTimeInput): { startAt: string | null; endAt: string | null; allDay: boolean } => {
	const startDateStr = toDateString(startDate);
	const endDateStr = toDateString(endDate);

	if (!startDateStr || !endDateStr) {
		return { startAt: startDateStr, endAt: endDateStr, allDay: true };
	}

	const normalizedStart = normalizeTime(startTime);
	const normalizedEnd = normalizeTime(endTime);
	const timed = Boolean(normalizedStart && normalizedEnd);

	if (!timed) {
		return {
			startAt: startDateStr,
			endAt: endDateStr,
			allDay: true,
		};
	}

	return {
		startAt: moment(`${startDateStr}T${normalizedStart}`).format('YYYY-MM-DDTHH:mm:ss'),
		endAt: moment(`${endDateStr}T${normalizedEnd}`).format('YYYY-MM-DDTHH:mm:ss'),
		allDay: false,
	};
};

const isMidnight = (value: moment.Moment): boolean =>
	value.hour() === 0 && value.minute() === 0 && value.second() === 0;

export const isTimedTimeOff = (
	startAt?: string | null,
	endAt?: string | null,
	allDay?: boolean | null
): boolean => {
	if (allDay === true) return false;
	if (!startAt || !endAt) return false;
	if (DATE_ONLY.test(String(startAt).trim()) && DATE_ONLY.test(String(endAt).trim())) {
		return false;
	}

	const start = moment(startAt);
	const end = moment(endAt);
	if (!start.isValid() || !end.isValid()) return false;

	const startsAtMidnight = isMidnight(start);
	const endsAtEndOfDay = end.hour() === 23 && end.minute() >= 59;
	// Inclusive all-day ranges are often stored as midnight-to-midnight on the
	// same day (zero duration) or midnight of a later day.
	const endsAtMidnightBoundary = isMidnight(end) && !end.isBefore(start, 'day');

	return !(startsAtMidnight && (endsAtEndOfDay || endsAtMidnightBoundary));
};

export const formatTimeOffPeriod = (
	startAt?: string | null,
	endAt?: string | null
): string => {
	if (!startAt || !endAt) return 'Not specified';

	const start = moment(startAt);
	const end = moment(endAt);
	if (!start.isValid() || !end.isValid()) return 'Not specified';

	if (!isTimedTimeOff(startAt, endAt)) {
		return `${start.format('MM/DD/YYYY')} - ${end.format('MM/DD/YYYY')}`;
	}

	if (start.isSame(end, 'day')) {
		return `${start.format('MM/DD/YYYY')} ${start.format('h:mm A')} - ${end.format('h:mm A')}`;
	}

	return `${start.format('MM/DD/YYYY h:mm A')} - ${end.format('MM/DD/YYYY h:mm A')}`;
};

export const formatTimeOffScheduleLabel = (
	startAt?: string | null,
	endAt?: string | null
): string => {
	if (!startAt || !endAt) return 'Time Off';

	const start = moment(startAt);
	const end = moment(endAt);
	if (!start.isValid() || !end.isValid()) return 'Time Off';

	if (!isTimedTimeOff(startAt, endAt)) {
		if (start.isSame(end, 'day')) return start.format('MMM D, YYYY');
		return `${start.format('MMM D')} - ${end.format('MMM D, YYYY')}`;
	}

	if (start.isSame(end, 'day')) {
		return `${start.format('MMM D, YYYY')} · ${start.format('h:mm A')} - ${end.format('h:mm A')}`;
	}

	return `${start.format('MMM D, YYYY h:mm A')} - ${end.format('MMM D, YYYY h:mm A')}`;
};

export const resolveTimeOffRange = (
	startAt?: string | null,
	endAt?: string | null,
	allDay?: boolean | null
): { start: moment.Moment; end: moment.Moment } | null => {
	if (!startAt || !endAt) return null;

	const start = moment(startAt);
	const end = moment(endAt);
	if (!start.isValid() || !end.isValid()) return null;

	if (!isTimedTimeOff(startAt, endAt, allDay)) {
		return {
			start: start.clone().startOf('day'),
			end: end.clone().endOf('day'),
		};
	}

	return { start, end };
};

export const timeOffOverlapsRange = (
	timeOffStart?: string | null,
	timeOffEnd?: string | null,
	rangeStart?: string | Date | null,
	rangeEnd?: string | Date | null
): boolean => {
	const timeOff = resolveTimeOffRange(timeOffStart, timeOffEnd);
	if (!timeOff || !rangeStart || !rangeEnd) return false;

	const rangeAsTimeOff = resolveTimeOffRange(
		typeof rangeStart === 'string' ? rangeStart : moment(rangeStart).format('YYYY-MM-DDTHH:mm:ss'),
		typeof rangeEnd === 'string' ? rangeEnd : moment(rangeEnd).format('YYYY-MM-DDTHH:mm:ss')
	);
	const otherStart = rangeAsTimeOff?.start ?? moment(rangeStart);
	const otherEnd = rangeAsTimeOff?.end ?? moment(rangeEnd);
	if (!otherStart.isValid() || !otherEnd.isValid()) return false;

	return timeOff.start.isBefore(otherEnd) && otherStart.isBefore(timeOff.end);
};

type TimeOffReasonRef = {
	id?: number | string;
	['@id']?: string;
	description?: string | null;
	note?: string | null;
};

export type TimeOffFormValues = {
	startDate: string;
	endDate: string;
	startTime: string;
	endTime: string;
	reason: string;
	notes: string;
	reasonDescription: string;
	reasonNote: string;
};

const collectReasonKeys = (reasonRef: unknown): string[] => {
	if (reasonRef == null || reasonRef === '') return [];

	const values: Array<string | number> = [];
	if (typeof reasonRef === 'string' || typeof reasonRef === 'number') {
		values.push(reasonRef);
	} else if (typeof reasonRef === 'object') {
		const reason = reasonRef as TimeOffReasonRef;
		if (reason['@id']) values.push(reason['@id']);
		if (reason.id != null) values.push(reason.id);
	}

	const keys = new Set<string>();
	values.forEach((value) => {
		const raw = String(value).trim();
		if (!raw) return;

		keys.add(raw);
		const noLeadingSlash = raw.replace(/^\//, '');
		keys.add(noLeadingSlash);
		keys.add(`/${noLeadingSlash}`);

		const idMatch = noLeadingSlash.match(/(\d+)$/);
		if (idMatch) {
			const id = idMatch[1];
			keys.add(id);
			keys.add(`/api/scheduler_time_off_reasons/${id}`);
			keys.add(`api/scheduler_time_off_reasons/${id}`);
		}
	});

	return [...keys];
};

const extractReasonIri = (reasonValue: unknown): string => {
	if (!reasonValue) return '';
	if (typeof reasonValue === 'string') return reasonValue;
	if (typeof reasonValue === 'object') {
		const reason = reasonValue as TimeOffReasonRef;
		if (reason['@id']) return reason['@id'];
		if (reason.id != null) return String(reason.id);
	}
	return '';
};

export const findTimeOffReason = <T extends TimeOffReasonRef>(
	reasons: T[] = [],
	reasonRef?: unknown
): T | null => {
	const keys = new Set(collectReasonKeys(reasonRef));
	if (keys.size === 0) return null;

	return (
		reasons.find((reason) => {
			if (reason['@id'] && keys.has(reason['@id'])) return true;
			if (reason.id != null && keys.has(String(reason.id))) return true;
			return collectReasonKeys(reason).some((key) => keys.has(key));
		}) || null
	);
};

export const extractTimeOffFormValues = (
	request?: {
		startAt?: string | null;
		endAt?: string | null;
		note?: string | null;
		reason?: string | { ['@id']?: string; id?: number | string } | null;
		reasonDescription?: string | null;
		reasonNote?: string | null;
		timeOffReasons?:
			| string
			| {
					['@id']?: string;
					id?: number | string;
					description?: string | null;
					note?: string | null;
			  }
			| null;
	} | null
): TimeOffFormValues => {
	const reasonValue = request?.timeOffReasons || request?.reason;
	const reason = extractReasonIri(reasonValue);
	const reasonObject =
		reasonValue && typeof reasonValue === 'object'
			? (reasonValue as TimeOffReasonRef)
			: null;
	const reasonDescription =
		request?.reasonDescription || reasonObject?.description || '';
	const reasonNote = request?.reasonNote || reasonObject?.note || '';
	const notes = request?.note || '';

	if (!request?.startAt || !request?.endAt) {
		return {
			startDate: '',
			endDate: '',
			startTime: '',
			endTime: '',
			reason,
			notes,
			reasonDescription,
			reasonNote,
		};
	}

	const start = moment(request.startAt);
	const end = moment(request.endAt);
	const timed = isTimedTimeOff(request.startAt, request.endAt);

	return {
		startDate: start.isValid() ? start.format('YYYY-MM-DD') : '',
		endDate: end.isValid() ? end.format('YYYY-MM-DD') : '',
		startTime: timed && start.isValid() ? start.format('HH:mm') : '',
		endTime: timed && end.isValid() ? end.format('HH:mm') : '',
		reason,
		notes,
		reasonDescription,
		reasonNote,
	};
};

export const hasTimeOffScheduleChanged = (
	original: Pick<TimeOffFormValues, 'startDate' | 'endDate' | 'startTime' | 'endTime'>,
	current: Pick<TimeOffFormValues, 'startDate' | 'endDate' | 'startTime' | 'endTime'>
): boolean =>
	original.startDate !== current.startDate ||
	original.endDate !== current.endDate ||
	(original.startTime || '') !== (current.startTime || '') ||
	(original.endTime || '') !== (current.endTime || '');
